import styled from '@emotion/styled';
import {
  type AllReadsQuery,
  DBSortOrder,
  DBTimestampType,
  RacemapAPIClient,
} from '@racemap/utilities/api-client';
import { OneDayInMillis, OneMinuteInMillis } from '@racemap/utilities/consts/time';
import type { Reader } from '@racemap/utilities/types/types';
import type { Immutable } from 'immer';
import { pick } from 'lodash';
import { type FC, useEffect } from 'react';
import { type Entry, TabsNavigation } from 'src/components/BasicComponents/Tabs/TabsNavigation';
import { ParametersSubTab } from 'src/components/Prediction/ParametersSubTab';
import PredictionSetupGuide from 'src/components/Prediction/PredictionSetupGuide';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import useSWR from 'swr';
import type { CurrentEvent } from '../../../store/events/events_reducers';
import type { ReadsFilter } from '../../../store/predictionStore';
import { useStore } from '../../../store/reducers';
import { AnalyseSubTab } from '../../Prediction/AnalyseSubTab';
import { ReadersSubTab } from '../../Prediction/ReadersSubTab';
import { ReadsSubTab } from '../../Prediction/ReadsSubTab';
import { StatusSubTab } from '../../Prediction/StatusSubTab';
import { TranspondersSubTab } from '../../Prediction/TranspondersSubTab';

export type PredictionTabKeys =
  | 'status'
  | 'guide'
  | 'readers'
  | 'transponders'
  | 'reads'
  | 'analyse'
  | 'parameters';
type TabEntry = Entry & { key: PredictionTabKeys };

// if the event is passed we choose the event time otherwise we choose the current time
function getSearchWindowEndTimeStamp(event: Immutable<CurrentEvent> | null): string {
  if (event == null || event.endTime == null) return new Date().toISOString();
  const endTime = new Date(event.endTime);
  if (endTime.valueOf() > Date.now()) return new Date().toISOString();
  return endTime.toISOString();
}

const apiClient = RacemapAPIClient.fromWindowLocation();

export const PredictionTab: FC = () => {
  const event = useCurrentEvent();
  const { setReadsFilter, loadShadowtrack } = useStore((s) => s.prediction.actions);
  const { loadEvent } = useStore((s) => s.events.actions);
  const readsFilter = useStore((s) => s.prediction.readsFilter);

  useEffect(() => {
    if (event == null) return;
    if (readsFilter.eventId === event.id) return;

    (async () => {
      const searchWindowEndTimeStamp = getSearchWindowEndTimeStamp(event);

      setReadsFilter({
        eventId: event.id,
        startTime: new Date(new Date(searchWindowEndTimeStamp).valueOf() - 5 * OneDayInMillis),
        endTime: new Date(searchWindowEndTimeStamp),
        timestampFilterType: DBTimestampType.ReceivedAt,
        timestampSortType: DBTimestampType.ReceivedAt,
        order: DBSortOrder.Descending,
        userIds: [...(event.integrations.raceresult.customerIds || []), ...event.editorIds],
        limit: 100,
        offset: 0,
      });

      await loadShadowtrack(event.id);

      // TODO: Why is this needed?
      if (event.parent != null) {
        loadEvent(event.parent);
      }
    })();
  }, [event, loadEvent, loadShadowtrack, readsFilter.eventId, setReadsFilter]);

  const predictionIsEnabled = event?.modules.predictive.enabled;

  const { data: readsCount, isLoading: isLoadingReadsCount } = useSWR<number>(
    predictionIsEnabled ? ['ReadsCount', readsFilter] : null, // this is used as an key and therefore we need the string constant to seperate it from the following useSWR call
    async ([, readsFilter]: [string, ReadsFilter]) => {
      const countResult = await apiClient
        .prediction()
        .getAllReadsByEventCount(
          pick(readsFilter, [
            'eventId',
            'startTime',
            'endTime',
            'timestampFilterType',
            'userIds',
            'isTest',
          ]),
        );

      return countResult.count;
    },
    { refreshInterval: OneMinuteInMillis, keepPreviousData: false },
  );

  // For Readers we just use readAt as filter criteria and no reads beeing testReads
  const readersFilter: AllReadsQuery = {
    eventId: readsFilter.eventId,
    startTime: readsFilter.startTime,
    endTime: readsFilter.endTime,
    timestampFilterType: DBTimestampType.ReadAt,
    userIds: readsFilter.userIds != null ? [...readsFilter.userIds] : undefined,
    isTest: false,
  };

  // this is also used in the ReadersTab - but should be cached by useSWR
  const { data: readers, isLoading: isLoadingReadersCount } = useSWR<Array<Reader>>(
    predictionIsEnabled ? ['Readers', readersFilter] : null,
    async ([, readersFilter]: [string, AllReadsQuery]) => {
      const readers = await apiClient.prediction().getAllReadersForEvent(readersFilter);

      return readers;
    },
    { refreshInterval: OneMinuteInMillis, keepPreviousData: false },
  );

  const isLoading = isLoadingReadsCount || isLoadingReadersCount || false;

  const tabs: Array<TabEntry> = [
    {
      key: 'status',
      label: 'Status',
      component: <StatusSubTab />,
    },
    {
      key: 'guide',
      label: 'Guide',
      component: <PredictionSetupGuide />,
    },
    {
      key: 'readers',
      label: `Readers ${readers?.length != null && readers.length > 0 ? `(${readers.length})` : ''}`,
      component: <ReadersSubTab />,
    },
    {
      key: 'reads',
      label: `Reads ${readsCount != null && readsCount > 0 ? `(${readsCount})` : ''}`,
      component: <ReadsSubTab />,
    },
    {
      key: 'transponders',
      label: `Transponders ${
        event?.starters?.size != null && event?.starters?.size > 0
          ? `(${event?.starters?.size})`
          : ''
      }`,
      component: <TranspondersSubTab />,
    },
    {
      key: 'analyse',
      label: 'Analysis',
      component: <AnalyseSubTab />,
    },
    {
      key: 'parameters',
      label: 'Parameters',
      component: <ParametersSubTab />,
    },
  ];

  return (
    <Container>
      <TabsNavigation entries={tabs} isLoading={isLoading} sticky={true} />
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;
